/**
 * Ordered section grouping for the change-request review. The backend emits a
 * flat list of field changes (each with a dotted `field` path); this maps them
 * into the wizard's logical sections, in the sequence the reviewer expects.
 *
 * Group types:
 *   - 'scalars'  : plain Field / Current / Requested rows
 *   - 'address'  : present + permanent shown as two labelled sub-tables
 *   - 'sections' : several labelled sub-tables (Passport / Bank / Attachments)
 *   - 'list'     : one repeating list (children, nominees, …) → per-entry tables
 */

const startsWithAny = (field, prefixes) =>
    prefixes.some((p) => field === p || field.startsWith(p));

export const CHANGE_GROUPS = [
    {
        key: 'general',
        title: 'General Information',
        type: 'scalars',
        fields: [
            'initial', 'firstname', 'middlename', 'lastname', 'nameInBengali',
            'gender', 'religion', 'bloodGroup', 'dateOfBirth', 'placeOfBirth',
            'maritalStatus'
        ]
    },
    {
        key: 'parents',
        title: 'Parents Information',
        type: 'scalars',
        prefixes: ['father.', 'mother.']
    },
    {
        key: 'address',
        title: 'Address',
        type: 'address',
        prefixes: ['contact.presentAddress.', 'contact.permanentAddress.'],
        subsections: [
            { title: 'Present Address', prefixes: ['contact.presentAddress.'] },
            { title: 'Permanent Address', prefixes: ['contact.permanentAddress.'] }
        ]
    },
    {
        key: 'personal',
        title: 'Personal Information',
        type: 'scalars',
        fields: ['contact.personalEmail', 'contact.mobileNumber']
    },
    {
        key: 'emergency',
        title: 'Emergency Contact',
        type: 'scalars',
        prefixes: ['contact.emergencyContact.']
    },
    {
        key: 'marriage',
        title: 'Marriage',
        type: 'scalars',
        fields: ['family.isMarried', 'family.marriageDate'],
        prefixes: ['family.spouse.']
    },
    { key: 'children', title: 'Children', type: 'list', listField: 'family.children' },
    { key: 'relatives', title: 'Relatives', type: 'list', listField: 'relatedEmployees' },
    {
        key: 'identification',
        title: 'Passport, Bank & Documents',
        type: 'sections',
        prefixes: ['identification.', 'bank.'],
        subsections: [
            {
                title: 'Passport Information',
                fields: [
                    'identification.passport.number',
                    'identification.passport.issuePlace',
                    'identification.passport.issueDate',
                    'identification.passport.expiryDate',
                    'identification.tin',
                    'identification.nationality',
                    'identification.nidNumber',
                    'identification.drivingLicenseNumber'
                ]
            },
            { title: 'Others (Bank)', prefixes: ['bank.'] },
            {
                title: 'Attachments',
                fields: [
                    'identification.photograph',
                    'identification.signature',
                    'identification.nid',
                    'identification.etin'
                ]
            }
        ]
    },
    { key: 'academic', title: 'Academic Qualifications', type: 'list', listField: 'academic.institutions' },
    { key: 'workExperiences', title: 'Work Experience', type: 'list', listField: 'workExperiences' },
    { key: 'nominees', title: 'Nominee Information', type: 'list', listField: 'nominees' },
    { key: 'references', title: 'References', type: 'list', listField: 'references' },
    { key: 'insurances', title: 'Insurance Information', type: 'list', listField: 'insurances' }
];

const matchesGroup = (group, field) => {
    if (group.type === 'list') return field === group.listField;
    if (group.fields && group.fields.includes(field)) return true;
    if (group.prefixes && startsWithAny(field, group.prefixes)) return true;
    return false;
};

const matchesSubsection = (sub, field) => {
    if (sub.fields && sub.fields.includes(field)) return true;
    if (sub.prefixes && startsWithAny(field, sub.prefixes)) return true;
    return false;
};

/**
 * Splits the flat `changes` into ordered, non-empty groups. Each returned group
 * carries its member changes (and, for address/section groups, per-subsection
 * members). Anything unmatched lands in a trailing "Other" group so nothing is
 * ever dropped from the reviewer's view.
 */
export const groupChanges = (changes = []) => {
    const used = new Set();
    const groups = [];

    CHANGE_GROUPS.forEach((group) => {
        const members = changes.filter((c) => matchesGroup(group, c.field));
        if (!members.length) return;
        members.forEach((c) => used.add(c.field));

        const entry = { ...group, members };
        if (group.type === 'address' || group.type === 'sections') {
            entry.sections = (group.subsections || [])
                .map((sub) => ({
                    title: sub.title,
                    members: members.filter((c) => matchesSubsection(sub, c.field))
                }))
                .filter((s) => s.members.length);
        }
        groups.push(entry);
    });

    const leftover = changes.filter((c) => !used.has(c.field));
    if (leftover.length) {
        groups.push({ key: 'other', title: 'Other', type: 'scalars', members: leftover });
    }
    return groups;
};
